import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Badge,
  Box,
  Breadcrumb,
  Button,
  Card,
  Dialog,
  Grid,
  HStack,
  Icon,
  Input,
  Select,
  SimpleGrid,
  Spinner,
  Stack,
  Switch,
  Tabs,
  Text,
  createListCollection,
} from "@chakra-ui/react";
import {
  MdChevronRight,
  MdEdit,
  MdEventAvailable,
  MdFamilyRestroom,
  MdLocalFireDepartment,
  MdGroups,
  MdHowToReg,
  MdInfo,
  MdPersonOutline,
  MdPhone,
  MdSchool,
  MdStar,
} from "react-icons/md";
import { HiCheck, HiX } from "react-icons/hi";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import {
  useDashboardSocio,
  useDashboardSocioActivityOverview,
  usePatchDashboardSocio,
} from "features/dashboard/socios/hooks";
import {
  useAddDashboardActividadAsistenciaSocio,
  useAddDashboardActividadPreceptoria,
  useDashboardActividadAsistenciaSocios,
  useDashboardActividadPreceptorias,
  useDashboardActividadRosters,
  useRemoveDashboardActividadAsistenciaSocio,
  useRemoveDashboardActividadPreceptoria,
} from "features/dashboard/actividades/hooks";
import { formatFechaRange } from "utils/formatters";
import { getAvatarHexColor, resolveAvatarUrl } from "utils/avatar";
import { getAccentHexColor, getAccentTint } from "utils/accentPalette";
import AppAvatar from "components/utils/AppAvatar";
import { TagCombobox } from "components/utils/TagCombobox";
import { storage } from "lib/firebase";
import { dashboardTabTriggerProps } from "layouts/Dashboard/tabStyles";
import { DashboardInput, DashboardSelectContent, DashboardSelectItem, DashboardSelectTrigger } from "components/utils/FormControls";
import { FormDialog } from "components/utils/FormDialog";

const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024;
const DEFAULT_ACTIVITY_COVER_URL =
  "https://images.unsplash.com/photo-1521412644187-c49fa049e84d?auto=format&fit=crop&w=1200&q=60";
const EMPTY = "—";

const TAB_LIST = [
  { value: "info", label: "Info", TabIcon: MdInfo },
  { value: "actividades", label: "Actividades", TabIcon: MdEventAvailable },
  { value: "preceptorias", label: "Preceptorias", TabIcon: MdGroups },
];

const FIXED_ACCENTS = {
  profile: { solid: "arawaks.skyBlue", cssVar: "var(--chakra-colors-arawaks-sky-blue)" },
  attendance: { solid: "arawaks.jungleGreen", cssVar: "var(--chakra-colors-arawaks-jungle-green)" },
  absence: { solid: "arawaks.blueGray", cssVar: "var(--chakra-colors-arawaks-blue-gray)" },
  preceptorias: { solid: "arawaks.tropicalTeal", cssVar: "var(--chakra-colors-arawaks-tropical-teal)" },
  preceptores: { solid: "arawaks.grapePurple", cssVar: "var(--chakra-colors-arawaks-grape-purple)" },
  family: { solid: "arawaks.rose", cssVar: "var(--chakra-colors-arawaks-rose)" },
  school: { solid: "arawaks.skyBlue", cssVar: "var(--chakra-colors-arawaks-sky-blue)" },
  generation: { solid: "arawaks.adventureOrange", cssVar: "var(--chakra-colors-arawaks-adventure-orange)" },
  streak: { solid: "arawaks.campfireRed", cssVar: "var(--chakra-colors-arawaks-campfire-red)" },
  status: { solid: "arawaks.goldenYellow", cssVar: "var(--chakra-colors-arawaks-golden-yellow)" },
};

function accentSolid(tone) {
  return typeof tone === "object" && tone?.solid ? tone.solid : getAccentHexColor(tone);
}

function accentTint(tone, strength = 12) {
  if (typeof tone === "object" && tone?.cssVar) {
    return `color-mix(in srgb, ${tone.cssVar} ${strength}%, transparent)`;
  }
  return getAccentTint(tone, strength);
}

function accentCssVar(tone) {
  return typeof tone === "object" && tone?.cssVar ? tone.cssVar : `var(--chakra-colors-${String(accentSolid(tone)).replace(/\./g, "-")})`;
}

function formatPercent(value) {
  return value === null || value === undefined ? EMPTY : `${value}%`;
}

function formatStreak(value) {
  const n = Number(value || 0);
  return `${n} ${n === 1 ? "actividad" : "actividades"}`;
}

function getFileExtension(file) {
  const base = (file?.name ?? "").split(".").pop()?.toLowerCase() ?? "";
  const cleaned = base.replace(/[^a-z0-9]/g, "");
  if (cleaned) return cleaned;
  if (file?.type === "image/jpeg") return "jpg";
  if (file?.type === "image/png") return "png";
  if (file?.type === "image/webp") return "webp";
  return "jpg";
}

const toStr = (v) => (v == null ? "" : String(v));
const toNull = (v) => { const t = String(v ?? "").trim(); return t.length ? t : null; };

function CompactInfoItem({ icon: Icon, value }) {
  if (!value) return null;
  return (
    <HStack gap="2" minW="0" justify="center">
      <Box flexShrink={0} color="dashboard.fg.subtle">
        <Icon size="15px" />
      </Box>
      <Text fontSize="sm" fontWeight="700" color="dashboard.fg.default" lineClamp={1}>
        {value}
      </Text>
    </HStack>
  );
}

function FactLine({ label, value, icon: IconCmp, tone }) {
  const solid = accentSolid(tone);
  return (
    <HStack gap="2.5" minW="0">
      <Box display="grid" placeItems="center" boxSize="7" rounded="full" bg={accentTint(tone, 14)} color={solid} flexShrink={0}>
        <IconCmp size="15px" />
      </Box>
      <Box minW="0">
        <Text fontSize="xs" color="dashboard.fg.muted" fontWeight="800" textTransform="uppercase" lineClamp={1}>
          {label}
        </Text>
        <Text fontSize="sm" color="dashboard.fg.default" fontWeight="800" lineClamp={1}>
          {value || EMPTY}
        </Text>
      </Box>
    </HStack>
  );
}

function DetailCard({ title, icon: IconCmp, tone, children }) {
  const solid = accentSolid(tone);
  return (
    <Box bg="dashboard.bg.surface" rounded="card" borderWidth="1px" borderColor="dashboard.border.muted" p="5" minW="0">
      <HStack gap="2.5" mb="4">
        <Box display="grid" placeItems="center" boxSize="8" rounded="lg" bg={accentTint(tone, 12)} color={solid} flexShrink={0}>
          <IconCmp size="17px" />
        </Box>
        <Text fontWeight="900" color="dashboard.fg.default" fontSize="sm">
          {title}
        </Text>
      </HStack>
      {children}
    </Box>
  );
}

function DonutSegment({ segment, circumference, offset, radius, strokeWidth, active, hasActive, onHover, onLeave }) {
  if (!segment.value) return null;
  const length = (segment.percent / 100) * circumference;
  return (
    <circle
      cx="80"
      cy="80"
      r={radius}
      fill="none"
      stroke={segment.color}
      strokeWidth={active ? strokeWidth + 4 : strokeWidth}
      strokeDasharray={`${length} ${circumference - length}`}
      strokeDashoffset={-offset}
      strokeLinecap="butt"
      transform="rotate(-90 80 80)"
      cursor="pointer"
      opacity={active || !hasActive ? 1 : 0.36}
      pointerEvents="visibleStroke"
      style={{ transition: "stroke-width 140ms ease, opacity 140ms ease" }}
      onMouseEnter={() => onHover(segment)}
      onMouseLeave={onLeave}
      onFocus={() => onHover(segment)}
      onBlur={onLeave}
      tabIndex={0}
    />
  );
}

function DonutChart({ segments, centerLabel, centerValue, emptyText = "Sin datos registrados." }) {
  const [activeSegment, setActiveSegment] = useState(null);
  const radius = 58;
  const strokeWidth = 18;
  const circumference = 2 * Math.PI * radius;
  const total = segments.reduce((sum, segment) => sum + Number(segment.value || 0), 0);
  let offset = 0;
  const active = activeSegment || null;

  if (total <= 0) {
    return <Text fontSize="sm" color="dashboard.fg.muted">{emptyText}</Text>;
  }

  return (
    <Grid templateColumns={{ base: "1fr", md: "220px 1fr" }} gap="5" alignItems="center">
      <Box position="relative" boxSize="220px" mx={{ base: "auto", md: "0" }}>
        <Box as="svg" viewBox="0 0 160 160" w="full" h="full">
          <circle cx="80" cy="80" r={radius} fill="none" stroke="var(--chakra-colors-dashboard-border-muted)" strokeWidth={strokeWidth} />
          {segments.map((segment) => {
            const currentOffset = offset;
            offset += (segment.percent / 100) * circumference;
            return (
              <DonutSegment
                key={segment.label}
                segment={segment}
                circumference={circumference}
                offset={currentOffset}
                radius={radius}
                strokeWidth={strokeWidth}
                active={active?.label === segment.label}
                hasActive={!!active}
                onHover={setActiveSegment}
                onLeave={() => setActiveSegment(null)}
              />
            );
          })}
        </Box>
        <Box position="absolute" inset="0" display="grid" placeItems="center" textAlign="center" pointerEvents="none">
          <Box>
            <Text fontFamily="display" fontSize="4xl" fontWeight="700" lineHeight="1" color="dashboard.fg.default">
              {active ? active.value : centerValue}
            </Text>
            <Text fontSize="xs" color="dashboard.fg.muted" fontWeight="800" textTransform="uppercase" mt="1">
              {active ? active.label : centerLabel}
            </Text>
            {active ? (
              <Text fontSize="xs" color="dashboard.fg.subtle" fontWeight="700" mt="1">
                {Math.round(active.percent)}% del total
              </Text>
            ) : null}
          </Box>
        </Box>
      </Box>

      <Stack gap="3">
        {segments.map((segment) => {
          const isActive = active?.label === segment.label;
          return (
            <HStack
              key={segment.label}
              gap="2.5"
              minW="0"
              cursor={segment.value > 0 ? "pointer" : "default"}
              rounded="lg"
              px="2"
              py="1.5"
              bg={isActive ? "dashboard.bg.surfaceMuted" : "transparent"}
              opacity={active && !isActive ? 0.46 : 1}
              onMouseEnter={() => segment.value > 0 && setActiveSegment(segment)}
              onMouseLeave={() => setActiveSegment(null)}
              onFocus={() => segment.value > 0 && setActiveSegment(segment)}
              onBlur={() => setActiveSegment(null)}
              tabIndex={segment.value > 0 ? 0 : undefined}
            >
              <Box boxSize="2.5" rounded="full" bg={segment.color} flexShrink={0} />
              <Box minW="0">
                <Text fontSize="sm" color="dashboard.fg.default" fontWeight="900" lineClamp={1}>
                  {segment.label}
                </Text>
                <Text fontSize="xs" color="dashboard.fg.muted" fontWeight="700">
                  {segment.value} - {Math.round(segment.percent)}%
                </Text>
              </Box>
            </HStack>
          );
        })}
      </Stack>
    </Grid>
  );
}

function buildSegments(rawSegments) {
  const total = rawSegments.reduce((sum, segment) => sum + Number(segment.value || 0), 0);
  return rawSegments.map((segment) => ({
    ...segment,
    color: accentCssVar(segment.tone),
    percent: total > 0 ? (Number(segment.value || 0) / total) * 100 : 0,
  }));
}

function SegmentedBarChart({
  segments,
  totalValue,
  summaryLabel,
  summaryValue,
  summarySecondary,
  formatActiveSecondary,
  emptyText = "Sin datos registrados.",
}) {
  const [activeSegment, setActiveSegment] = useState(null);
  const active = activeSegment || null;
  const total = Number(totalValue || 0);

  if (total <= 0) {
    return <Text fontSize="sm" color="dashboard.fg.muted">{emptyText}</Text>;
  }

  return (
    <Stack gap="4">
      <HStack justify="space-between" align="end" gap="4">
        <Box minW="0">
          <Text fontSize="xs" color="dashboard.fg.muted" fontWeight="800" textTransform="uppercase">
            {active ? active.label : summaryLabel}
          </Text>
          <Text fontFamily="display" fontSize="3xl" color="dashboard.fg.default" fontWeight="700" lineHeight="1" mt="1">
            {active ? active.primary : summaryValue}
          </Text>
        </Box>
        <Text fontSize="xs" color="dashboard.fg.muted" fontWeight="800" textAlign="right" maxW="44">
          {active ? formatActiveSecondary?.(active, total) || active.secondary : summarySecondary || `${total} registros`}
        </Text>
      </HStack>

      <HStack gap="1" h="16" rounded="2xl" overflow="hidden" bg="dashboard.bg.surfaceMuted" p="1">
        {segments.map((segment) => {
          const width = `${Math.max(0, (Number(segment.value || 0) / total) * 100)}%`;
          const isActive = active?.label === segment.label;
          return (
            <Box
              key={segment.label}
              h="full"
              minW={segment.value > 0 ? "8px" : "0"}
              flex={`0 0 ${width}`}
              bg={segment.color}
              opacity={isActive || !active ? 1 : 0.42}
              rounded="xl"
              cursor={segment.value > 0 ? "pointer" : "default"}
              transition="opacity 140ms ease, transform 140ms ease"
              transform={isActive ? "translateY(-2px)" : "none"}
              onMouseEnter={() => segment.value > 0 && setActiveSegment(segment)}
              onMouseLeave={() => setActiveSegment(null)}
              onFocus={() => segment.value > 0 && setActiveSegment(segment)}
              onBlur={() => setActiveSegment(null)}
              tabIndex={segment.value > 0 ? 0 : undefined}
            />
          );
        })}
      </HStack>

      <SimpleGrid columns={{ base: 1, sm: 2 }} gap="3">
        {segments.map((segment) => {
          const isActive = active?.label === segment.label;
          return (
            <HStack
              key={segment.label}
              gap="2.5"
              minW="0"
              cursor={segment.value > 0 ? "pointer" : "default"}
              rounded="lg"
              px="2"
              py="1.5"
              bg={isActive ? "dashboard.bg.surfaceMuted" : "transparent"}
              onMouseEnter={() => segment.value > 0 && setActiveSegment(segment)}
              onMouseLeave={() => setActiveSegment(null)}
              onFocus={() => segment.value > 0 && setActiveSegment(segment)}
              onBlur={() => setActiveSegment(null)}
              tabIndex={segment.value > 0 ? 0 : undefined}
            >
              <Box boxSize="2.5" rounded="full" bg={segment.color} flexShrink={0} />
              <Box minW="0">
                <Text fontSize="sm" color="dashboard.fg.default" fontWeight="800" lineClamp={1}>
                  {segment.label}
                </Text>
                <Text fontSize="xs" color="dashboard.fg.muted" fontWeight="700">
                  {segment.primary} - {Math.round((Number(segment.value || 0) / total) * 100)}%
                </Text>
              </Box>
            </HStack>
          );
        })}
      </SimpleGrid>
    </Stack>
  );
}

function SocioConsistencyHeatmap({ activities = [] }) {
  const [activeItem, setActiveItem] = useState(null);
  const sortedActivities = [...activities].sort((a, b) => String(a.fechaInicio || "").localeCompare(String(b.fechaInicio || "")));
  const items = sortedActivities.map((activity) => {
    const status = activity.asistio ? "asistencia" : "falta";
    const tone = activity.asistio ? FIXED_ACCENTS.attendance : FIXED_ACCENTS.absence;
    return {
      id: activity.actividadId,
      nombre: activity.nombre || "Actividad",
      fechaInicio: activity.fechaInicio,
      fechaFin: activity.fechaFin,
      status,
      tone,
      color: accentCssVar(tone),
    };
  });
  const counts = items.reduce((acc, item) => {
    acc[item.status] += 1;
    return acc;
  }, { asistencia: 0, falta: 0 });
  const absenceFlags = items.map((item) => item.status === "falta");
  let maxAbsenceStreak = 0;
  let currentAbsenceStreak = 0;
  absenceFlags.forEach((isAbsence) => {
    currentAbsenceStreak = isAbsence ? currentAbsenceStreak + 1 : 0;
    maxAbsenceStreak = Math.max(maxAbsenceStreak, currentAbsenceStreak);
  });
  const attendancePct = items.length ? Math.round((counts.asistencia / items.length) * 100) : null;
  const recentAbsences = absenceFlags.slice(-3).filter(Boolean).length;
  const consistency = attendancePct === null ? null : Math.max(0, Math.min(100, attendancePct - Math.max(0, maxAbsenceStreak - 1) * 8 - recentAbsences * 5));
  const consistencyLabel =
    consistency === null ? EMPTY : consistency >= 90 ? "Excelente" : consistency >= 75 ? "Buena" : consistency >= 60 ? "Regular" : consistency >= 40 ? "Baja" : "Critica";
  const active = activeItem;

  if (!items.length) {
    return <Text fontSize="sm" color="dashboard.fg.muted">Sin actividades registradas.</Text>;
  }

  return (
    <Stack gap="4">
      <HStack justify="space-between" align="end" gap="4">
        <Box minW="0" flex="1">
          <Text fontSize="xs" color="dashboard.fg.muted" fontWeight="800" textTransform="uppercase" truncate>
            {active ? active.nombre : "Consistencia"}
          </Text>
          <Text fontFamily="display" fontSize="3xl" color="dashboard.fg.default" fontWeight="700" lineHeight="1" mt="1">
            {active ? (active.status === "asistencia" ? "Asistencia" : "Falta") : formatPercent(consistency)}
          </Text>
        </Box>
        <Text fontSize="xs" color="dashboard.fg.muted" fontWeight="800" textAlign="right" maxW="48">
          {active ? formatFechaRange({ fechaInicio: active.fechaInicio, fechaFin: active.fechaFin }) : `${consistencyLabel} - ${items.length} actividades`}
        </Text>
      </HStack>

      <Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(14px, 1fr))" gap="2" alignItems="center">
        {items.map((item) => {
          const isActive = active?.id === item.id;
          return (
            <Box
              key={item.id}
              aspectRatio="1"
              minW="14px"
              rounded="sm"
              bg={item.color}
              opacity={active && !isActive ? 0.36 : 1}
              outline={isActive ? "2px solid" : "0"}
              outlineColor={item.color}
              outlineOffset="2px"
              cursor="pointer"
              transition="opacity 140ms ease, transform 140ms ease, outline 140ms ease"
              transform={isActive ? "translateY(-2px)" : "none"}
              onMouseEnter={() => setActiveItem(item)}
              onMouseLeave={() => setActiveItem(null)}
              onFocus={() => setActiveItem(item)}
              onBlur={() => setActiveItem(null)}
              tabIndex={0}
            />
          );
        })}
      </Box>

      <HStack gap="3" wrap="wrap">
        {[
          { label: "Asistencias", value: counts.asistencia, tone: FIXED_ACCENTS.attendance },
          { label: "Faltas", value: counts.falta, tone: FIXED_ACCENTS.absence },
        ].map((item) => (
          <HStack key={item.label} gap="2">
            <Box boxSize="2.5" rounded="full" bg={accentCssVar(item.tone)} />
            <Text fontSize="xs" color="dashboard.fg.default" fontWeight="800">{item.label}</Text>
            <Text fontSize="xs" color="dashboard.fg.muted" fontWeight="800">{item.value}</Text>
          </HStack>
        ))}
      </HStack>
    </Stack>
  );
}

function SocioProfileBand({ socio, onEdit, attendanceStreak = 0 }) {
  const clickable = typeof onEdit === "function";
  function handleKeyDown(e) {
    if (!clickable) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onEdit();
    }
  }

  return (
    <Box
      bg="dashboard.bg.surface"
      rounded="card"
      borderWidth="1px"
      borderColor="dashboard.border.muted"
      p={{ base: "4", md: "5" }}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      cursor={clickable ? "pointer" : "default"}
      onClick={clickable ? onEdit : undefined}
      onKeyDown={handleKeyDown}
      transition="box-shadow 150ms ease, transform 150ms ease, border-color 150ms ease"
      _hover={clickable ? { transform: "translateY(-2px)", boxShadow: "cardLift", borderColor: "dashboard.border.default" } : undefined}
      _focusVisible={{ outline: "2px solid", outlineColor: "dashboard.accent.border", outlineOffset: "2px" }}
    >
      <HStack justify="space-between" align="start" gap="4">
        <Box minW="0">
          <Text fontFamily="display" fontSize={{ base: "2xl", md: "3xl" }} fontWeight="700" color="dashboard.fg.default" lineClamp={1}>
            {socio?.nombre || "Socio"}
          </Text>
        </Box>
        {clickable ? (
          <Box display="grid" placeItems="center" boxSize="8" rounded="full" color="dashboard.fg.muted" flexShrink={0}>
            <MdEdit />
          </Box>
        ) : null}
      </HStack>
      <SimpleGrid columns={{ base: 1, sm: 2, xl: 4 }} gap={{ base: "3", xl: "4" }} mt="4">
        <FactLine label="Escuela" value={socio?.escuela} icon={MdSchool} tone={FIXED_ACCENTS.school} />
        <FactLine label="Generacion" value={socio?.generacion} icon={MdGroups} tone={FIXED_ACCENTS.generation} />
        <FactLine label="Racha asistencia" value={formatStreak(attendanceStreak)} icon={MdLocalFireDepartment} tone={FIXED_ACCENTS.streak} />
        <FactLine label="Estado" value={socio?.activo ? "Registrado" : "Inactivo"} icon={MdStar} tone={FIXED_ACCENTS.status} />
      </SimpleGrid>
    </Box>
  );
}

function ParentToggleCard({ mama, papa, onEditMama, onEditPapa }) {
  const [parentView, setParentView] = useState("mama");
  const parent = parentView === "mama" ? mama : papa;
  const onEdit = parentView === "mama" ? onEditMama : onEditPapa;
  const phoneSolid = accentSolid(FIXED_ACCENTS.school);
  const emailSolid = accentSolid(FIXED_ACCENTS.generation);

  return (
    <DetailCard title="Familia" icon={MdFamilyRestroom} tone={FIXED_ACCENTS.family}>
      <Stack gap="4">
        <Tabs.Root value={parentView} onValueChange={(e) => setParentView(e.value)} variant="subtle" colorPalette="red">
          <Tabs.List w="full">
            <Tabs.Trigger value="mama" flex="1">Mamá</Tabs.Trigger>
            <Tabs.Trigger value="papa" flex="1">Papá</Tabs.Trigger>
            <Tabs.Indicator rounded="md" />
          </Tabs.List>
        </Tabs.Root>

        <HStack justify="space-between" align="start" gap="3">
          <Text fontSize="md" fontWeight="900" color="dashboard.fg.default" lineClamp={1} minW="0">
            {parent.nombre || EMPTY}
          </Text>
          <Box
            as="button"
            type="button"
            display="grid"
            placeItems="center"
            boxSize="8"
            rounded="full"
            color="dashboard.fg.muted"
            flexShrink={0}
            onClick={onEdit}
            aria-label={`Editar ${parentView === "mama" ? "mamá" : "papá"}`}
            transition="background 150ms ease, color 150ms ease"
            _hover={{ bg: "dashboard.bg.surfaceMuted", color: "dashboard.accent.solid" }}
          >
            <MdEdit />
          </Box>
        </HStack>

        <Grid templateColumns={{ base: "1fr", sm: "1fr 1fr" }} gap="3">
          <HStack gap="2.5" minW="0">
            <Box display="grid" placeItems="center" boxSize="7" rounded="full" bg={accentTint(FIXED_ACCENTS.school, 14)} color={phoneSolid} flexShrink={0}>
              <MdPhone size="15px" />
            </Box>
            <Box minW="0">
              <Text fontSize="xs" color="dashboard.fg.muted" fontWeight="800" textTransform="uppercase">Telefono</Text>
              <Text fontSize="sm" color="dashboard.fg.default" fontWeight="800" lineClamp={1}>{parent.telefono || EMPTY}</Text>
            </Box>
          </HStack>

          <HStack gap="2.5" minW="0">
            <Box display="grid" placeItems="center" boxSize="7" rounded="full" bg={accentTint(FIXED_ACCENTS.generation, 14)} color={emailSolid} flexShrink={0}>
              <MdFamilyRestroom size="15px" />
            </Box>
            <Box minW="0">
              <Text fontSize="xs" color="dashboard.fg.muted" fontWeight="800" textTransform="uppercase">Correo</Text>
              <Text fontSize="sm" color="dashboard.fg.default" fontWeight="800" lineClamp={1}>{parent.correo || EMPTY}</Text>
            </Box>
          </HStack>
        </Grid>
      </Stack>
    </DetailCard>
  );
}

function SocioMetrics({ socio, activities, preceptorias, asistenciaPct, onEditPersonal, onEditMama, onEditPapa }) {
  const attendanceCount = activities.filter((activity) => !!activity.asistio).length;
  const absenceCount = Math.max(0, activities.length - attendanceCount);
  const attendanceSegments = buildSegments([
    { label: "Asistencias", value: attendanceCount, tone: FIXED_ACCENTS.attendance },
    { label: "Faltas", value: absenceCount, tone: FIXED_ACCENTS.absence },
  ]);
  const preceptorGroups = new Map();
  preceptorias.forEach((row) => {
    const key = String(row.instructorId ?? row.instructorNombre ?? "instructor");
    const current = preceptorGroups.get(key) || {
      label: row.instructorNombre || "Instructor",
      count: 0,
    };
    current.count += 1;
    preceptorGroups.set(key, current);
  });
  const preceptorSegments = Array.from(preceptorGroups.values()).map((item, index) => {
    const tone = index % 2 === 0 ? FIXED_ACCENTS.preceptorias : FIXED_ACCENTS.preceptores;
    return {
      label: item.label,
      value: item.count,
      color: accentCssVar(tone),
      primary: `${item.count} preceptorias`,
      secondary: `${item.count} con este preceptor`,
    };
  });
  const preceptorCount = preceptorGroups.size;
  const attendanceStreak = [...activities]
    .sort((a, b) => String(a.fechaInicio || "").localeCompare(String(b.fechaInicio || "")))
    .reduce((acc, activity) => {
      const current = activity.asistio ? acc.current + 1 : 0;
      return { current, longest: Math.max(acc.longest, current) };
    }, { current: 0, longest: 0 }).longest;

  return (
    <Stack gap="4">
      <SocioProfileBand socio={socio} onEdit={onEditPersonal} attendanceStreak={attendanceStreak} />

      <Grid templateColumns={{ base: "1fr", xl: "1fr 1fr" }} gap="4" alignItems="start">
        <Stack gap="4" minW="0">
          <DetailCard title="Asistencia" icon={MdEventAvailable} tone={FIXED_ACCENTS.attendance}>
            <DonutChart
              segments={attendanceSegments}
              centerLabel="Asistencia"
              centerValue={asistenciaPct != null ? `${asistenciaPct}%` : formatPercent(activities.length ? Math.round((attendanceCount / activities.length) * 100) : null)}
              emptyText="Sin asistencias registradas."
            />
          </DetailCard>

          <DetailCard title="Consistencia" icon={MdHowToReg} tone={FIXED_ACCENTS.school}>
            <SocioConsistencyHeatmap activities={activities} />
          </DetailCard>
        </Stack>

        <Stack gap="4" minW="0">
          <DetailCard title="Preceptorias" icon={MdGroups} tone={FIXED_ACCENTS.preceptorias}>
            <SegmentedBarChart
              segments={preceptorSegments}
              totalValue={preceptorias.length}
              summaryLabel="Total de preceptorias"
              summaryValue={preceptorias.length}
              summarySecondary={`${preceptorCount} ${preceptorCount === 1 ? "preceptor" : "preceptores"}`}
              formatActiveSecondary={(segment, total) => `${Math.round((Number(segment.value || 0) / total) * 100)}% del total`}
              emptyText="Sin preceptorias registradas."
            />
          </DetailCard>

          <ParentToggleCard
            mama={socio.mama}
            papa={socio.papa}
            onEditMama={onEditMama}
            onEditPapa={onEditPapa}
          />
        </Stack>
      </Grid>
    </Stack>
  );
}

function ActivityCard({ activity, onEdit }) {
  const asistio = !!activity.asistio;
  const openEdit = () => onEdit?.(activity);
  const preceptores = Array.isArray(activity.preceptores) ? activity.preceptores : [];

  return (
    <Card.Root
      bg={asistio ? "dashboard.bg.surface" : "dashboard.bg.surfaceMuted"}
      borderWidth="1px"
      borderColor={asistio ? "dashboard.border.muted" : "dashboard.border.default"}
      rounded="card"
      opacity={asistio ? 1 : 0.58}
      cursor="pointer"
      overflow="hidden"
      transition="transform 150ms ease, box-shadow 150ms ease, border-color 150ms ease"
      _hover={{ transform: "translateY(-2px)", boxShadow: "cardLift", borderColor: "dashboard.border.default" }}
      role="button"
      tabIndex={0}
      onClick={openEdit}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openEdit(); } }}
    >
      <Box display="flex" minH="120px">
        <Box w={{ base: "100px", md: "140px" }} flexShrink={0} position="relative" overflow="hidden">
          <Box
            as="img"
            src={activity.coverUrl || DEFAULT_ACTIVITY_COVER_URL}
            alt={activity.nombre || "Actividad"}
            w="100%" h="100%" objectFit="cover"
            filter={asistio ? "none" : "grayscale(1)"}
          />
          <Box position="absolute" inset="0" bg={asistio ? "blackAlpha.100" : "blackAlpha.400"} />
        </Box>

        <Box flex="1" p={{ base: "3", md: "4" }} display="flex" flexDirection="column" gap="2" minW="0">
          <HStack justify="space-between" align="start" gap="3">
            <Box minW="0">
              <Text fontFamily="display" fontSize="md" fontWeight="700" color="dashboard.fg.default" lineClamp={1}>
                {activity.nombre?.trim() || "Actividad sin nombre"}
              </Text>
              <Text fontSize="xs" color="dashboard.fg.muted">
                {formatFechaRange({ fechaInicio: activity.fechaInicio, fechaFin: activity.fechaFin })}
              </Text>
            </Box>
            <Badge colorPalette={asistio ? "green" : "gray"} variant="subtle" rounded="full" flexShrink={0}>
              {asistio ? "Asistió" : "No asistió"}
            </Badge>
          </HStack>

          {preceptores.length > 0 && (
            <Box>
              <Text fontSize="xs" color="dashboard.fg.subtle" fontWeight="800" textTransform="uppercase" mb="1">
                Preceptores
              </Text>
              <HStack gap="1" wrap="wrap">
                {preceptores.map((p) => (
                  <Badge key={p.instructorId} colorPalette="orange" variant="subtle" rounded="full" fontSize="xs">
                    {p.instructorNombre || "Instructor"}
                  </Badge>
                ))}
              </HStack>
            </Box>
          )}
        </Box>
      </Box>
    </Card.Root>
  );
}

function PreceptoriaCard({ group }) {
  return (
    <Card.Root bg="dashboard.bg.surface" borderWidth="1px" borderColor="dashboard.border.muted" rounded="card">
      <Card.Body gap="2">
        <HStack justify="space-between" align="start">
          <Box minW="0">
            <Text fontWeight="800" color="dashboard.fg.default">
              {group.instructorNombre || "Instructor"}
            </Text>
            <Text fontSize="sm" color="dashboard.fg.muted">
              {group.actividades.length} {group.actividades.length === 1 ? "actividad" : "actividades"}
            </Text>
          </Box>
          <Badge colorPalette="orange" variant="subtle" rounded="full">Preceptoria</Badge>
        </HStack>
        <Stack gap="2" mt="2">
          {group.actividades.map((actividad, index) => (
            <Box
              key={`${actividad.actividadId || actividad.actividadNombre || "actividad"}-${index}`}
              bg="dashboard.bg.surfaceMuted"
              rounded="lg"
              px="3"
              py="2"
              borderWidth="1px"
              borderColor="dashboard.border.muted"
            >
              <Text fontSize="sm" fontWeight="800" color="dashboard.fg.default" lineClamp={1}>
                {actividad.actividadNombre || "Actividad"}
              </Text>
              <Text fontSize="xs" color="dashboard.fg.subtle">
                {formatFechaRange({ fechaInicio: actividad.fechaInicio })}
              </Text>
            </Box>
          ))}
        </Stack>
      </Card.Body>
    </Card.Root>
  );
}

function QuickEditDialog({ open, onOpenChange, activity, socioId }) {
  const actividadId = activity?.actividadId;

  const rostersQ = useDashboardActividadRosters(open);
  const asistenciaQ = useDashboardActividadAsistenciaSocios(actividadId, open && !!actividadId);
  const preceptoriasQ = useDashboardActividadPreceptorias(actividadId, open && !!actividadId);

  const addAsistencia = useAddDashboardActividadAsistenciaSocio();
  const removeAsistencia = useRemoveDashboardActividadAsistenciaSocio();
  const addPreceptoria = useAddDashboardActividadPreceptoria();
  const removePreceptoria = useRemoveDashboardActividadPreceptoria();

  const [attended, setAttended] = useState(false);
  const [instructorIds, setInstructorIds] = useState([]);
  const [error, setError] = useState("");

  const asistenciaRows = useMemo(() => {
    const d = asistenciaQ.data;
    if (Array.isArray(d)) return d;
    if (Array.isArray(d?.rows)) return d.rows;
    return [];
  }, [asistenciaQ.data]);

  const preceptoriaRows = useMemo(() => (Array.isArray(preceptoriasQ.data) ? preceptoriasQ.data : []), [preceptoriasQ.data]);

  const asistenciaRow = useMemo(
    () => asistenciaRows.find((r) => String(r.socioId ?? r.id) === String(socioId)),
    [asistenciaRows, socioId]
  );

  const preceptoriasForSocio = useMemo(
    () => preceptoriaRows.filter((r) => String(r.socioId) === String(socioId)),
    [preceptoriaRows, socioId]
  );

  const instructores = useMemo(() => {
    const rows = rostersQ.data?.instructoresPeriodo || [];
    return Array.isArray(rows) ? rows : [];
  }, [rostersQ.data]);

  const instructorItems = useMemo(
    () => instructores
      .map((i) => ({ value: String(i.id || i.instructorId || ""), label: i.nombre || i.instructorNombre || "Instructor" }))
      .filter((i) => i.value),
    [instructores]
  );

  const loading = asistenciaQ.isLoading || preceptoriasQ.isLoading || rostersQ.isLoading;
  const saving = addAsistencia.isPending || removeAsistencia.isPending || addPreceptoria.isPending || removePreceptoria.isPending;

  useEffect(() => {
    if (!open || !activity) return;
    setError("");
    setAttended(!!asistenciaRow);
    setInstructorIds(preceptoriasForSocio.map((r) => String(r.instructorId)));
  }, [open, activity, asistenciaRow, preceptoriasForSocio]);

  async function save() {
    if (!actividadId || !socioId) return;
    setError("");
    try {
      if (!attended && asistenciaRow) {
        await removeAsistencia.mutateAsync({ actividadId, socioId });
      }
      if (attended && !asistenciaRow) {
        await addAsistencia.mutateAsync({ actividadId, socioId });
      }

      const prevIds = new Set(preceptoriasForSocio.map((r) => String(r.instructorId)));
      const nextIds = new Set(instructorIds.map(String));
      for (const instructorId of prevIds) {
        if (!nextIds.has(instructorId)) await removePreceptoria.mutateAsync({ actividadId, instructorId, socioId });
      }
      for (const instructorId of nextIds) {
        if (!prevIds.has(instructorId)) await addPreceptoria.mutateAsync({ actividadId, instructorId, socioId });
      }

      onOpenChange(false);
    } catch (e) {
      setError(String(e?.message || e));
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={(e) => onOpenChange(e.open)}>
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content maxW="lg" bg="dashboard.bg.dialog" color="dashboard.fg.default" borderWidth="1px" borderColor="dashboard.border.default">
          <Dialog.Header>
            <Dialog.Title>Editar en actividad</Dialog.Title>
          </Dialog.Header>

          <Dialog.Body>
            {loading ? (
              <Box py="8" display="flex" justifyContent="center"><Spinner /></Box>
            ) : (
              <Stack gap="5">
                <Box>
                  <Text fontFamily="display" fontWeight="700" fontSize="xl">{activity?.nombre || "Actividad"}</Text>
                  <Text color="dashboard.fg.muted" fontSize="sm">
                    {formatFechaRange({ fechaInicio: activity?.fechaInicio, fechaFin: activity?.fechaFin })}
                  </Text>
                </Box>

                <HStack justify="space-between" align="center" bg="dashboard.bg.surfaceMuted" rounded="lg" px="4" py="3">
                  <Box>
                    <Text fontWeight="700">Asistencia</Text>
                    <Text fontSize="sm" color="dashboard.fg.muted">¿El socio asistió?</Text>
                  </Box>
                  <Switch.Root
                    size="lg"
                    checked={attended}
                    onCheckedChange={(e) => {
                      const next = !!e.checked;
                      setAttended(next);
                      if (!next) setInstructorIds([]);
                    }}
                  >
                    <Switch.HiddenInput />
                    <Switch.Control>
                      <Switch.Thumb>
                        <Switch.ThumbIndicator fallback={<HiX color="black" />}>
                          <HiCheck />
                        </Switch.ThumbIndicator>
                      </Switch.Thumb>
                    </Switch.Control>
                  </Switch.Root>
                </HStack>

                {attended && (
                  <TagCombobox
                    label="Preceptorias recibidas"
                    placeholder="Instructor que dio preceptoria..."
                    items={instructorItems}
                    value={instructorIds}
                    onChange={setInstructorIds}
                    showAvatar={false}
                  />
                )}

                {error && (
                  <Text color="dashboard.feedback.error" whiteSpace="pre-wrap">{error}</Text>
                )}
              </Stack>
            )}
          </Dialog.Body>

          <Dialog.Footer>
            <HStack justify="space-between" w="full">
              <Link to={`/dashboard/club/actividades/${actividadId}`}>
                <Button variant="outline" disabled={!actividadId || saving}>Abrir actividad</Button>
              </Link>
              <HStack>
                <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancelar</Button>
                <Button colorPalette="red" onClick={save} disabled={saving || loading}>
                  {saving ? <Spinner size="sm" /> : null}
                  Guardar
                </Button>
              </HStack>
            </HStack>
          </Dialog.Footer>

          <Dialog.CloseTrigger />
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}

const SocioDetail = () => {
  const { id } = useParams();
  const avatarInputRef = useRef(null);

  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [avatarUploadError, setAvatarUploadError] = useState("");
  const [editSection, setEditSection] = useState(null);
  const [editingActivity, setEditingActivity] = useState(null);
  const [view, setView] = useState("info");

  // Edit form state
  const [fNombre, setFNombre] = useState("");
  const [fEscuela, setFEscuela] = useState("");
  const [fGeneracion, setFGeneracion] = useState("");
  const [fActivo, setFActivo] = useState("true");
  const [fMamaNombre, setFMamaNombre] = useState("");
  const [fMamaCorreo, setFMamaCorreo] = useState("");
  const [fMamaTelefono, setFMamaTelefono] = useState("");
  const [fPapaNombre, setFPapaNombre] = useState("");
  const [fPapaCorreo, setFPapaCorreo] = useState("");
  const [fPapaTelefono, setFPapaTelefono] = useState("");

  const q = useDashboardSocio(id, true);
  const actQ = useDashboardSocioActivityOverview(id, !!id);
  const patchM = usePatchDashboardSocio();
  const statusCollection = useMemo(
    () => createListCollection({
      items: [
        { label: "Registrado", value: "true" },
        { label: "Inactivo", value: "false" },
      ],
    }),
    []
  );

  const socio = q.data;
  const socioId = socio?.id ?? socio?.socioId ?? null;

  const nombre = useMemo(() => {
    const raw = socio?.nombre ?? socio?.socioNombre;
    return typeof raw === "string" && raw.trim() ? raw.trim() : "Socio";
  }, [socio]);

  const escuela = socio?.escuela ?? socio?.socioColegio ?? null;
  const generacion = socio?.generacion ?? socio?.socioGeneracion ?? null;
  const activo = socio?.activo ?? true;
  const asistenciaPct = typeof socio?.asistencia === "number" ? socio.asistencia : null;

  const mamaNombre = socio?.mamaNombre ?? socio?.nombreMama ?? null;
  const mamaCorreo = socio?.mamaCorreo ?? socio?.correoMama ?? null;
  const mamaTelefono = socio?.mamaTelefono ?? socio?.telefonoMama ?? null;
  const papaNombre = socio?.papaNombre ?? socio?.nombrePapa ?? null;
  const papaCorreo = socio?.papaCorreo ?? socio?.correoPapa ?? null;
  const papaTelefono = socio?.papaTelefono ?? socio?.telefonoPapa ?? null;

  const avatarSrc = resolveAvatarUrl(socio) || null;
  const avatarSeed = socio?.id ?? socio?.socioId ?? nombre;
  const avatarColor = getAvatarHexColor(avatarSeed);

  const activities = useMemo(() => (Array.isArray(actQ.data?.actividades) ? actQ.data.actividades : []), [actQ.data]);
  const preceptorias = useMemo(() => (Array.isArray(actQ.data?.preceptorias) ? actQ.data.preceptorias : []), [actQ.data]);
  const preceptoriasPorInstructor = useMemo(() => {
    const groups = new Map();
    preceptorias.forEach((row) => {
      const key = String(row.instructorId ?? row.instructorNombre ?? "instructor");
      const current = groups.get(key) || {
        instructorId: row.instructorId,
        instructorNombre: row.instructorNombre,
        actividades: [],
      };
      current.actividades.push({
        actividadId: row.actividadId,
        actividadNombre: row.actividadNombre,
        fechaInicio: row.fechaInicio,
      });
      groups.set(key, current);
    });
    return Array.from(groups.values()).map((group) => ({
      ...group,
      actividades: group.actividades.sort((a, b) => String(a.fechaInicio || "").localeCompare(String(b.fechaInicio || ""))),
    }));
  }, [preceptorias]);

  const saving = patchM.isPending;
  const formError = patchM.error;

  const onAvatarClick = () => { if (!isUploadingAvatar) avatarInputRef.current?.click(); };

  const onAvatarFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !id) return;
    if (!file.type?.startsWith("image/")) { setAvatarUploadError("Solo se permiten archivos de imagen."); return; }
    if (file.size > MAX_IMAGE_SIZE_BYTES) { setAvatarUploadError("El archivo supera el limite de 10MB."); return; }
    setAvatarUploadError("");
    setIsUploadingAvatar(true);
    try {
      const imageId = crypto.randomUUID();
      const ext = getFileExtension(file);
      const storagePath = `socios/${id}/avatar/${imageId}.${ext}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file, file.type ? { contentType: file.type } : undefined);
      const downloadUrl = await getDownloadURL(storageRef);
      await patchM.mutateAsync({
        socioId: id,
        patch: { fotoUrl: downloadUrl, fotoStoragePath: storagePath },
      });
    } catch (e) {
      setAvatarUploadError(String(e?.message || e || "No se pudo subir la foto."));
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const openPersonalEdit = () => {
    setFNombre(toStr(socio?.nombre ?? socio?.socioNombre));
    setFEscuela(toStr(socio?.escuela ?? socio?.socioColegio));
    setFGeneracion(toStr(socio?.generacion ?? socio?.socioGeneracion));
    setFActivo(String(socio?.activo ?? true));
    setEditSection("personal");
  };

  const openMamaEdit = () => {
    setFMamaNombre(toStr(socio?.mamaNombre ?? socio?.nombreMama));
    setFMamaCorreo(toStr(socio?.mamaCorreo ?? socio?.correoMama));
    setFMamaTelefono(toStr(socio?.mamaTelefono ?? socio?.telefonoMama));
    setEditSection("mama");
  };

  const openPapaEdit = () => {
    setFPapaNombre(toStr(socio?.papaNombre ?? socio?.nombrePapa));
    setFPapaCorreo(toStr(socio?.papaCorreo ?? socio?.correoPapa));
    setFPapaTelefono(toStr(socio?.papaTelefono ?? socio?.telefonoPapa));
    setEditSection("papa");
  };

  const closeEdit = () => setEditSection(null);

  const onSavePersonal = async () => {
    if (!socioId) return;
    await patchM.mutateAsync({
      socioId,
      patch: {
        nombre: toNull(fNombre),
        escuela: toNull(fEscuela),
        generacion: toNull(fGeneracion),
        activo: fActivo === "true",
      },
    });
    closeEdit();
  };

  const onSaveMama = async () => {
    if (!socioId) return;
    await patchM.mutateAsync({
      socioId,
      patch: {
        mamaNombre: toNull(fMamaNombre),
        mamaCorreo: toNull(fMamaCorreo),
        mamaTelefono: toNull(fMamaTelefono),
      },
    });
    closeEdit();
  };

  const onSavePapa = async () => {
    if (!socioId) return;
    await patchM.mutateAsync({
      socioId,
      patch: {
        papaNombre: toNull(fPapaNombre),
        papaCorreo: toNull(fPapaCorreo),
        papaTelefono: toNull(fPapaTelefono),
      },
    });
    closeEdit();
  };

  const breadcrumb = (
    <Breadcrumb.Root pb="2">
      <Breadcrumb.List>
        <Breadcrumb.Item>
          <Breadcrumb.Link asChild>
            <Link to="/dashboard/club/socios">
              <HStack><MdPersonOutline />Socios</HStack>
            </Link>
          </Breadcrumb.Link>
        </Breadcrumb.Item>
        <Breadcrumb.Separator><MdChevronRight /></Breadcrumb.Separator>
        <Breadcrumb.Item>
          <Breadcrumb.CurrentLink>{nombre}</Breadcrumb.CurrentLink>
        </Breadcrumb.Item>
      </Breadcrumb.List>
    </Breadcrumb.Root>
  );

  if (q.isLoading) {
    return (
      <Box>
        {breadcrumb}
        <Box bg="dashboard.bg.surface" rounded="card" p="8" display="flex" justifyContent="center"><Spinner /></Box>
      </Box>
    );
  }

  if (q.isError) {
    return (
      <Box>
        {breadcrumb}
        <Box bg="dashboard.bg.surface" rounded="card" p="6">
          <Text color="dashboard.feedback.error" fontWeight="bold">Error cargando socio</Text>
          <Text as="pre" whiteSpace="pre-wrap" color="dashboard.feedback.error" mt="2">
            {String(q.error?.message || q.error)}
          </Text>
        </Box>
      </Box>
    );
  }

  if (!socio) {
    return (
      <Box>
        {breadcrumb}
        <Box bg="dashboard.bg.surface" rounded="card" p="6">
          <Text color="dashboard.feedback.error" fontWeight="bold">Socio no encontrado en el periodo actual.</Text>
          <Text color="fg.muted" mt="2">Revisa que el periodo seleccionado en el dashboard sea el correcto.</Text>
        </Box>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap="5">
      {breadcrumb}

      <Tabs.Root
        value={view}
        onValueChange={(e) => setView(e.value)}
        orientation="vertical"
        variant="subtle"
        colorPalette="red"
        w="full"
        display="block"
      >
        <Grid templateColumns={{ base: "1fr", md: "270px 1fr" }} gap="5" alignItems="start">
          <Box
            bg="dashboard.bg.surface"
            rounded="card"
            borderWidth="1px"
            borderColor="dashboard.border.muted"
            overflow="hidden"
            position={{ md: "sticky" }}
            top={{ md: "4" }}
          >
            <Box position="relative">
              <Box h="2" bg={avatarColor} />
              <Box p={{ base: "4", md: "5" }}>
                <Stack gap="4" align="center" textAlign="center">
                  <Box>
                    <Input ref={avatarInputRef} type="file" accept="image/*" onChange={onAvatarFileChange} display="none" />
                    <AppAvatar
                      name={nombre} src={avatarSrc} size={84} seed={avatarSeed} color={avatarColor}
                      cursor={isUploadingAvatar ? "progress" : "pointer"}
                      onClick={onAvatarClick}
                    />
                    {isUploadingAvatar && <Text fontSize="xs" color="dashboard.fg.muted" mt="1" textAlign="center">Subiendo...</Text>}
                    {avatarUploadError && <Text fontSize="xs" color="dashboard.feedback.error" mt="1" maxW="84px">{avatarUploadError}</Text>}
                  </Box>

                  <Box w="full" minW="0">
                    <Text fontFamily="display" fontSize={{ base: "xl", md: "2xl" }} fontWeight="700" lineClamp={2} color="dashboard.fg.default" textAlign="center">
                      {nombre}
                    </Text>
                    <Box display="inline-flex" rounded="pill" px="2.5" py="0.5" bg={avatarColor} color="white" fontSize="xs" fontWeight="700" maxW="full" mt="2">
                      <HStack gap="1.5" minW="0" justify="center">
                        <Icon color="white" flexShrink={0}>
                          <MdPersonOutline />
                        </Icon>
                        <Text as="span" fontSize="xs" fontWeight="800" color="white" truncate>
                          {activo ? "Registrado" : "Inactivo"}
                        </Text>
                      </HStack>
                    </Box>
                  </Box>
                </Stack>
              </Box>
            </Box>

            <Tabs.List p="2" gap="0.5" flexDirection="column" bg="transparent" w="full">
              {TAB_LIST.map(({ value, label, TabIcon }) => {
                const isSelected = view === value;
                return (
                  <Tabs.Trigger key={value} value={value} w="full" {...dashboardTabTriggerProps}>
                    <HStack w="full" gap="2.5">
                      <Box
                        display="grid"
                        placeItems="center"
                        boxSize="6"
                        rounded="md"
                        flexShrink={0}
                        bg={isSelected ? "red.100" : "transparent"}
                        _dark={{ bg: isSelected ? "red.800" : "transparent" }}
                        transition="background 150ms ease"
                      >
                        <TabIcon size="15px" />
                      </Box>
                      <Text>{label}</Text>
                    </HStack>
                  </Tabs.Trigger>
                );
              })}
              <Tabs.Indicator rounded="md" />
            </Tabs.List>
          </Box>

          <Box minW="0">
            <Tabs.Content value="info" p="0">
              <SocioMetrics
                socio={{
                  nombre,
                  escuela,
                  generacion,
                  activo,
                  mama: { nombre: mamaNombre, correo: mamaCorreo, telefono: mamaTelefono },
                  papa: { nombre: papaNombre, correo: papaCorreo, telefono: papaTelefono },
                }}
                activities={activities}
                preceptorias={preceptorias}
                asistenciaPct={asistenciaPct}
                onEditPersonal={openPersonalEdit}
                onEditMama={openMamaEdit}
                onEditPapa={openPapaEdit}
              />
            </Tabs.Content>

      {asistenciaPct != null ? (
        <Box mb="4" display="none">
          <HStack align="center" gap="3">
            <Text fontSize="sm" fontWeight="800" color="dashboard.fg.muted">Asistencia</Text>
            <Text fontFamily="display" fontSize="2xl" lineHeight="1" fontWeight="700" color="dashboard.fg.default">
              {asistenciaPct}%
            </Text>
          </HStack>
          <Box h="6px" bg="dashboard.bg.surfaceMuted" borderRadius="full" overflow="hidden" mt="2">
            <Box
              h="100%"
              w={`${asistenciaPct}%`}
              bg={asistenciaPct >= 80 ? "green.500" : asistenciaPct >= 50 ? "yellow.500" : "red.500"}
              borderRadius="full"
              transition="width 0.4s"
            />
          </Box>
        </Box>
      ) : null}

      {/* Profile card + Asistencia card */}
      <Grid templateColumns={{ base: "1fr", lg: "1fr 240px" }} gap="5" alignItems="stretch" display="none">

        {/* Profile card */}
        <Box
          bg="dashboard.bg.surface"
          rounded="card"
          borderWidth="1px"
          borderColor="dashboard.border.muted"
          overflow="hidden"
        >
          <Box h="2" bg="teal.600" />
          <Box p={{ base: "4", md: "5" }}>
            <HStack gap={{ base: "4", md: "5" }} align="start">
              <Box flexShrink={0}>
                <Input ref={avatarInputRef} type="file" accept="image/*" onChange={onAvatarFileChange} display="none" />
                <AppAvatar
                  name={nombre} src={avatarSrc} size={84} seed={avatarSeed}
                  cursor={isUploadingAvatar ? "progress" : "pointer"}
                  onClick={onAvatarClick}
                />
                {isUploadingAvatar && <Text fontSize="xs" color="dashboard.fg.muted" mt="1" textAlign="center">Subiendo...</Text>}
                {avatarUploadError && <Text fontSize="xs" color="dashboard.feedback.error" mt="1" maxW="84px">{avatarUploadError}</Text>}
              </Box>

              <Box flex="1" minW="0">
                <HStack justify="space-between" align="start" gap="3">
                  <Box minW="0">
                    <Text fontFamily="display" fontSize={{ base: "xl", md: "2xl" }} fontWeight="700" lineClamp={1} color="dashboard.fg.default">
                      {nombre}
                    </Text>
                    <Badge colorPalette={activo ? "green" : "gray"} variant="subtle" mt="1">
                      {activo ? "Registrado" : "Inactivo"}
                    </Badge>
                  </Box>
                  <Button size="sm" variant="outline" onClick={openPersonalEdit} flexShrink={0}>
                    Editar
                  </Button>
                </HStack>

                <Grid templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }} gap="3" mt="4">
                  <CompactInfoItem icon={MdSchool} value={escuela} />
                  <CompactInfoItem icon={MdSchool} value={generacion} />
                </Grid>

                {(mamaNombre || papaNombre) && (
                  <Box mt="4" pt="3" borderTopWidth="1px" borderColor="dashboard.border.muted">
                    <HStack gap="1.5" mb="2" color="dashboard.fg.muted">
                      <MdFamilyRestroom size="14px" />
                      <Text fontSize="xs" fontWeight="800" textTransform="uppercase" letterSpacing="0.06em">Familia</Text>
                    </HStack>
                    <Grid templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }} gap="3">
                      {mamaNombre && (
                        <Stack gap="0">
                          <Text fontSize="xs" color="dashboard.fg.muted" textTransform="uppercase" fontWeight="700" letterSpacing="0.06em">Mamá</Text>
                          <Text fontWeight="600" fontSize="sm">{mamaNombre}</Text>
                          {mamaTelefono && (
                            <HStack gap="1" mt="0.5">
                              <MdPhone size="12px" color="var(--chakra-colors-dashboard-fg-muted)" />
                              <Text fontSize="xs" color="dashboard.fg.muted">{mamaTelefono}</Text>
                            </HStack>
                          )}
                        </Stack>
                      )}
                      {papaNombre && (
                        <Stack gap="0">
                          <Text fontSize="xs" color="dashboard.fg.muted" textTransform="uppercase" fontWeight="700" letterSpacing="0.06em">Papá</Text>
                          <Text fontWeight="600" fontSize="sm">{papaNombre}</Text>
                          {papaTelefono && (
                            <HStack gap="1" mt="0.5">
                              <MdPhone size="12px" color="var(--chakra-colors-dashboard-fg-muted)" />
                              <Text fontSize="xs" color="dashboard.fg.muted">{papaTelefono}</Text>
                            </HStack>
                          )}
                        </Stack>
                      )}
                    </Grid>
                  </Box>
                )}
              </Box>
            </HStack>
          </Box>
        </Box>

        {/* Asistencia % card */}
        <Box
          bg="dashboard.bg.surface"
          rounded="card"
          borderWidth="1px"
          borderColor="dashboard.border.muted"
          overflow="hidden"
          display="flex"
          flexDirection="column"
        >
          <Box h="2" bg="teal.600" />
          <Box p="5" flex="1" display="flex" flexDirection="column" justifyContent="center">
            <HStack gap="1.5" mb="3" color="dashboard.fg.muted">
              <MdEventAvailable size="16px" />
              <Text fontSize="xs" fontWeight="800" textTransform="uppercase" letterSpacing="0.06em">Asistencia</Text>
            </HStack>
            <HStack align="baseline" gap="1" mb="3">
              <Text fontFamily="display" fontSize="5xl" lineHeight="1" fontWeight="700" color="dashboard.fg.default">
                {asistenciaPct != null ? asistenciaPct : EMPTY}
              </Text>
              {asistenciaPct != null && (
                <Text fontSize="lg" color="dashboard.fg.muted" fontWeight="600">%</Text>
              )}
            </HStack>
            {asistenciaPct != null && (
              <Box>
                <Box h="6px" bg="dashboard.bg.surfaceMuted" borderRadius="full" overflow="hidden">
                  <Box
                    h="100%"
                    w={`${asistenciaPct}%`}
                    bg={asistenciaPct >= 80 ? "green.500" : asistenciaPct >= 50 ? "yellow.500" : "red.500"}
                    borderRadius="full"
                    transition="width 0.4s"
                  />
                </Box>
                <Text fontSize="xs" color="dashboard.fg.muted" mt="1.5">del periodo actual</Text>
              </Box>
            )}
          </Box>
        </Box>
      </Grid>

      {/* Activity tabs */}
      {actQ.isLoading ? (
        <Box bg="dashboard.bg.surface" rounded="card" p="8" display="flex" justifyContent="center">
          <Spinner />
        </Box>
      ) : actQ.isError ? (
        <Box bg="dashboard.bg.surface" rounded="card" p="6">
          <Text color="dashboard.feedback.error" fontWeight="bold">No se pudo cargar el historial.</Text>
        </Box>
      ) : (
        <Tabs.Root value={view} onValueChange={(e) => setView(e.value)} variant="subtle" colorPalette="red">
          <Tabs.List mb="4" display="none">
            <Tabs.Trigger value="actividades">Actividades</Tabs.Trigger>
            <Tabs.Trigger value="preceptorias">Preceptorias</Tabs.Trigger>
            <Tabs.Indicator rounded="md" />
          </Tabs.List>

          <Tabs.Content value="actividades" p="0">
            {activities.length === 0 ? (
              <Text color="fg.muted">Sin actividades registradas.</Text>
            ) : (
              <SimpleGrid columns={{ base: 1, xl: 2 }} gap="4">
                {activities.map((a) => (
                  <ActivityCard key={a.actividadId} activity={a} onEdit={setEditingActivity} />
                ))}
              </SimpleGrid>
            )}
          </Tabs.Content>

          <Tabs.Content value="preceptorias" p="0">
            {preceptoriasPorInstructor.length === 0 ? (
              <Text color="fg.muted">Sin preceptorias registradas.</Text>
            ) : (
              <SimpleGrid columns={{ base: 1, xl: 2 }} gap="4">
                {preceptoriasPorInstructor.map((group) => (
                  <PreceptoriaCard key={group.instructorId ?? group.instructorNombre} group={group} />
                ))}
              </SimpleGrid>
            )}
          </Tabs.Content>
        </Tabs.Root>
      )}

          </Box>
        </Grid>
      </Tabs.Root>

      {/* Activity quick-edit dialog */}
      <QuickEditDialog
        open={!!editingActivity}
        onOpenChange={(nextOpen) => { if (!nextOpen) setEditingActivity(null); }}
        activity={editingActivity}
        socioId={id}
      />

      <FormDialog
        open={editSection === "personal"}
        onOpenChange={(e) => { if (!e.open) closeEdit(); }}
        title="Editar Información Personal"
        errorText={formError ? String(formError?.message || formError) : ""}
        saving={saving}
        onCancel={closeEdit}
        onSave={onSavePersonal}
      >
        <Box>
          <Text fontSize="sm" fontWeight="600" color="dashboard.fg.default" mb="1">Nombre</Text>
          <DashboardInput value={fNombre} onChange={(e) => setFNombre(e.target.value)} placeholder="Nombre completo" />
        </Box>
        <Grid templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }} gap="3">
          <Box>
            <Text fontSize="sm" fontWeight="600" color="dashboard.fg.default" mb="1">Escuela</Text>
            <DashboardInput value={fEscuela} onChange={(e) => setFEscuela(e.target.value)} placeholder="Ej. TEC de Monterrey" />
          </Box>
          <Box>
            <Text fontSize="sm" fontWeight="600" color="dashboard.fg.default" mb="1">Generacion</Text>
            <DashboardInput value={fGeneracion} onChange={(e) => setFGeneracion(e.target.value)} placeholder="Ej. 2024" />
          </Box>
        </Grid>
        <Select.Root collection={statusCollection} size="sm" value={fActivo ? [fActivo] : []} onValueChange={(e) => setFActivo(e.value?.[0] ?? "true")}>
          <Select.HiddenSelect />
          <Select.Label fontSize="sm" fontWeight="600" color="dashboard.fg.default">Estado</Select.Label>
          <Select.Control>
            <DashboardSelectTrigger>
              <Select.ValueText placeholder="Estado del socio" />
            </DashboardSelectTrigger>
            <Select.IndicatorGroup>
              <Select.Indicator color="dashboard.accent.solid" />
            </Select.IndicatorGroup>
          </Select.Control>
          <Select.Positioner>
            <DashboardSelectContent>
              {statusCollection.items.map((status) => (
                <DashboardSelectItem item={status} key={status.value}>
                  {status.label}
                  <Select.ItemIndicator />
                </DashboardSelectItem>
              ))}
            </DashboardSelectContent>
          </Select.Positioner>
        </Select.Root>
      </FormDialog>

      <FormDialog
        open={editSection === "mama"}
        onOpenChange={(e) => { if (!e.open) closeEdit(); }}
        title="Editar Mamá"
        errorText={formError ? String(formError?.message || formError) : ""}
        saving={saving}
        onCancel={closeEdit}
        onSave={onSaveMama}
      >
        <Box>
          <Text fontSize="sm" fontWeight="600" color="dashboard.fg.default" mb="1">Nombre</Text>
          <DashboardInput value={fMamaNombre} onChange={(e) => setFMamaNombre(e.target.value)} placeholder="Nombre" />
        </Box>
        <Grid templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }} gap="3">
          <Box>
            <Text fontSize="sm" fontWeight="600" color="dashboard.fg.default" mb="1">Telefono</Text>
            <DashboardInput type="tel" value={fMamaTelefono} onChange={(e) => setFMamaTelefono(e.target.value)} placeholder="81 1234 5678" />
          </Box>
          <Box>
            <Text fontSize="sm" fontWeight="600" color="dashboard.fg.default" mb="1">Correo</Text>
            <DashboardInput type="email" value={fMamaCorreo} onChange={(e) => setFMamaCorreo(e.target.value)} placeholder="correo@ejemplo.com" />
          </Box>
        </Grid>
      </FormDialog>

      <FormDialog
        open={editSection === "papa"}
        onOpenChange={(e) => { if (!e.open) closeEdit(); }}
        title="Editar Papá"
        errorText={formError ? String(formError?.message || formError) : ""}
        saving={saving}
        onCancel={closeEdit}
        onSave={onSavePapa}
      >
        <Box>
          <Text fontSize="sm" fontWeight="600" color="dashboard.fg.default" mb="1">Nombre</Text>
          <DashboardInput value={fPapaNombre} onChange={(e) => setFPapaNombre(e.target.value)} placeholder="Nombre" />
        </Box>
        <Grid templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }} gap="3">
          <Box>
            <Text fontSize="sm" fontWeight="600" color="dashboard.fg.default" mb="1">Telefono</Text>
            <DashboardInput type="tel" value={fPapaTelefono} onChange={(e) => setFPapaTelefono(e.target.value)} placeholder="81 1234 5678" />
          </Box>
          <Box>
            <Text fontSize="sm" fontWeight="600" color="dashboard.fg.default" mb="1">Correo</Text>
            <DashboardInput type="email" value={fPapaCorreo} onChange={(e) => setFPapaCorreo(e.target.value)} placeholder="correo@ejemplo.com" />
          </Box>
        </Grid>
      </FormDialog>

    </Box>
  );
};

export default SocioDetail;
